import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calculator, TrendingUp, ArrowDownCircle, PiggyBank, Home } from 'lucide-react';

const Calculators = () => {
  // SIP Calculator State
  const [sipMonthly, setSipMonthly] = useState(5000);
  const [sipRate, setSipRate] = useState(12);
  const [sipYears, setSipYears] = useState(10);
  const [sipResult, setSipResult] = useState(null);

  // SWP Calculator State
  const [swpInvestment, setSwpInvestment] = useState(1000000);
  const [swpWithdrawal, setSwpWithdrawal] = useState(10000);
  const [swpRate, setSwpRate] = useState(8);
  const [swpYears, setSwpYears] = useState(10);
  const [swpResult, setSwpResult] = useState(null);

  // Retirement Calculator State
  const [retireAge, setRetireAge] = useState(60);
  const [currentAge, setCurrentAge] = useState(30);
  const [monthlyExpense, setMonthlyExpense] = useState(50000);
  const [inflation, setInflation] = useState(6);
  const [retireResult, setRetireResult] = useState(null);

  // EMI Calculator State
  const [loanAmount, setLoanAmount] = useState(1000000);
  const [loanRate, setLoanRate] = useState(8.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [emiResult, setEmiResult] = useState(null);

  // SIP Calculator Logic
  const calculateSIP = () => {
    const monthlyInvestment = parseFloat(sipMonthly);
    const annualRate = parseFloat(sipRate);
    const years = parseFloat(sipYears);
    
    const monthlyRate = annualRate / 12 / 100;
    const months = years * 12;
    
    const futureValue = monthlyInvestment * 
      (((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate));
    
    const invested = monthlyInvestment * months;
    const returns = futureValue - invested;
    
    setSipResult({
      invested: invested.toFixed(0),
      returns: returns.toFixed(0),
      total: futureValue.toFixed(0)
    });
  };

  // SWP Calculator Logic
  const calculateSWP = () => {
    const initialInvestment = parseFloat(swpInvestment);
    const monthlyWithdrawal = parseFloat(swpWithdrawal);
    const annualRate = parseFloat(swpRate);
    const years = parseFloat(swpYears);
    
    const monthlyRate = annualRate / 12 / 100;
    const months = years * 12;
    
    let balance = initialInvestment;
    let totalWithdrawn = 0;
    
    for (let i = 0; i < months; i++) {
      balance = balance * (1 + monthlyRate) - monthlyWithdrawal;
      totalWithdrawn += monthlyWithdrawal;
      if (balance < 0) {
        setSwpResult({ error: "Investment depleted before tenure ends. Reduce withdrawal or increase investment." });
        return;
      }
    }
    
    setSwpResult({
      initialInvestment: initialInvestment.toFixed(0),
      totalWithdrawn: totalWithdrawn.toFixed(0),
      finalBalance: balance.toFixed(0)
    });
  };

  // Retirement Calculator Logic
  const calculateRetirement = () => {
    const currentAgeVal = parseFloat(currentAge);
    const retirementAge = parseFloat(retireAge);
    const monthlyExp = parseFloat(monthlyExpense);
    const inflationRate = parseFloat(inflation);
    
    const yearsToRetirement = retirementAge - currentAgeVal;
    
    // Future value of monthly expenses at retirement
    const futureMonthlyExpense = monthlyExp * Math.pow(1 + inflationRate / 100, yearsToRetirement);
    
    // Annual expenses at retirement
    const annualExpenseAtRetirement = futureMonthlyExpense * 12;
    
    // Corpus needed (assuming 25x annual expenses for 30 years post-retirement)
    const corpusNeeded = annualExpenseAtRetirement * 25;
    
    // Monthly SIP needed (assuming 12% returns)
    const monthlyRate = 12 / 12 / 100;
    const months = yearsToRetirement * 12;
    const monthlySIP = corpusNeeded / (((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate));
    
    setRetireResult({
      yearsToRetirement: yearsToRetirement,
      futureMonthlyExpense: futureMonthlyExpense.toFixed(0),
      corpusNeeded: corpusNeeded.toFixed(0),
      monthlySIP: monthlySIP.toFixed(0)
    });
  };

  // EMI Calculator Logic
  const calculateEMI = () => {
    const principal = parseFloat(loanAmount);
    const annualRate = parseFloat(loanRate);
    const years = parseFloat(loanTenure);
    
    const monthlyRate = annualRate / 12 / 100;
    const months = years * 12;
    
    const emi = principal * monthlyRate * Math.pow(1 + monthlyRate, months) / 
                (Math.pow(1 + monthlyRate, months) - 1);
    
    const totalPayment = emi * months;
    const totalInterest = totalPayment - principal;
    
    setEmiResult({
      emi: emi.toFixed(0),
      totalPayment: totalPayment.toFixed(0),
      totalInterest: totalInterest.toFixed(0),
      principal: principal.toFixed(0)
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-[#0f1419] pt-20">
      <div className="container mx-auto px-4 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4">
            Financial <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">Calculators</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Plan your financial future with our easy-to-use calculators
          </p>
        </div>

        <Tabs defaultValue="sip" className="max-w-5xl mx-auto">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-8 h-auto bg-[#1a1f2e] border border-gray-800">
            <TabsTrigger value="sip" className="py-3 text-base data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black">
              <TrendingUp className="w-4 h-4 mr-2" />
              SIP
            </TabsTrigger>
            <TabsTrigger value="swp" className="py-3 text-base data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black">
              <ArrowDownCircle className="w-4 h-4 mr-2" />
              SWP
            </TabsTrigger>
            <TabsTrigger value="retirement" className="py-3 text-base data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black">
              <PiggyBank className="w-4 h-4 mr-2" />
              Retirement
            </TabsTrigger>
            <TabsTrigger value="emi" className="py-3 text-base data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black">
              <Home className="w-4 h-4 mr-2" />
              EMI
            </TabsTrigger>
          </TabsList>

          {/* SIP Calculator */}
          <TabsContent value="sip">
            <Card className="bg-[#1a1f2e] border-2 border-gray-800">
              <CardHeader>
                <CardTitle className="text-3xl flex items-center text-white">
                  <Calculator className="w-8 h-8 mr-3 text-[#F59E0B]" />
                  SIP Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="sipMonthly" className="text-base text-gray-300">Monthly Investment (₹)</Label>
                      <Input
                        id="sipMonthly"
                        type="number"
                        value={sipMonthly}
                        onChange={(e) => setSipMonthly(e.target.value)}
                        className="mt-2 text-base h-12 bg-[#0f1419] border-gray-700 text-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="sipRate" className="text-base text-gray-300">Expected Return Rate (% per annum)</Label>
                      <Input
                        id="sipRate"
                        type="number"
                        value={sipRate}
                        onChange={(e) => setSipRate(e.target.value)}
                        className="mt-2 text-base h-12 bg-[#0f1419] border-gray-700 text-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="sipYears" className="text-base text-gray-300">Investment Period (Years)</Label>
                      <Input
                        id="sipYears"
                        type="number"
                        value={sipYears}
                        onChange={(e) => setSipYears(e.target.value)}
                        className="mt-2 text-base h-12 bg-[#0f1419] border-gray-700 text-white"
                      />
                    </div>
                    <Button 
                      onClick={calculateSIP}
                      className="w-full bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] hover:from-[#D97706] hover:to-[#F59E0B] text-black font-semibold h-12 text-base"
                    >
                      Calculate SIP Returns
                    </Button>
                  </div>
                  <div>
                    {sipResult && (
                      <div className="bg-gradient-to-br from-[#F59E0B] to-[#FBBF24] p-8 rounded-xl text-black">
                        <h3 className="text-2xl font-bold mb-6">Results</h3>
                        <div className="space-y-4">
                          <div>
                            <p className="text-sm opacity-80">Total Invested</p>
                            <p className="text-3xl font-bold">{formatCurrency(sipResult.invested)}</p>
                          </div>
                          <div>
                            <p className="text-sm opacity-80">Estimated Returns</p>
                            <p className="text-3xl font-bold">{formatCurrency(sipResult.returns)}</p>
                          </div>
                          <div className="pt-4 border-t border-black/30">
                            <p className="text-sm opacity-80">Maturity Value</p>
                            <p className="text-4xl font-bold">{formatCurrency(sipResult.total)}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SWP Calculator */}
          <TabsContent value="swp">
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-3xl flex items-center">
                  <Calculator className="w-8 h-8 mr-3 text-[#0E6E6E]" />
                  SWP Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="swpInvestment" className="text-base">Total Investment (₹)</Label>
                      <Input
                        id="swpInvestment"
                        type="number"
                        value={swpInvestment}
                        onChange={(e) => setSwpInvestment(e.target.value)}
                        className="mt-2 text-base h-12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="swpWithdrawal" className="text-base">Monthly Withdrawal (₹)</Label>
                      <Input
                        id="swpWithdrawal"
                        type="number"
                        value={swpWithdrawal}
                        onChange={(e) => setSwpWithdrawal(e.target.value)}
                        className="mt-2 text-base h-12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="swpRate" className="text-base">Expected Return Rate (% per annum)</Label>
                      <Input
                        id="swpRate"
                        type="number"
                        value={swpRate}
                        onChange={(e) => setSwpRate(e.target.value)}
                        className="mt-2 text-base h-12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="swpYears" className="text-base">Withdrawal Period (Years)</Label>
                      <Input
                        id="swpYears"
                        type="number"
                        value={swpYears}
                        onChange={(e) => setSwpYears(e.target.value)}
                        className="mt-2 text-base h-12"
                      />
                    </div>
                    <Button 
                      onClick={calculateSWP}
                      className="w-full bg-[#0E6E6E] hover:bg-[#0E6E6E]/90 text-white h-12 text-base"
                    >
                      Calculate SWP
                    </Button>
                  </div>
                  <div>
                    {swpResult && (
                      <div className="bg-gradient-to-br from-[#0E6E6E] to-[#0a5555] p-8 rounded-xl text-white">
                        {swpResult.error ? (
                          <div>
                            <h3 className="text-2xl font-bold mb-4">Alert</h3>
                            <p className="text-base">{swpResult.error}</p>
                          </div>
                        ) : (
                          <>
                            <h3 className="text-2xl font-bold mb-6">Results</h3>
                            <div className="space-y-4">
                              <div>
                                <p className="text-sm text-gray-200">Initial Investment</p>
                                <p className="text-3xl font-bold">{formatCurrency(swpResult.initialInvestment)}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-200">Total Withdrawn</p>
                                <p className="text-3xl font-bold">{formatCurrency(swpResult.totalWithdrawn)}</p>
                              </div>
                              <div className="pt-4 border-t border-white/30">
                                <p className="text-sm text-gray-200">Final Balance</p>
                                <p className="text-4xl font-bold">{formatCurrency(swpResult.finalBalance)}</p>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Retirement Calculator */}
          <TabsContent value="retirement">
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-3xl flex items-center">
                  <Calculator className="w-8 h-8 mr-3 text-[#0E6E6E]" />
                  Retirement Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="currentAge" className="text-base">Current Age</Label>
                      <Input
                        id="currentAge"
                        type="number"
                        value={currentAge}
                        onChange={(e) => setCurrentAge(e.target.value)}
                        className="mt-2 text-base h-12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="retireAge" className="text-base">Retirement Age</Label>
                      <Input
                        id="retireAge"
                        type="number"
                        value={retireAge}
                        onChange={(e) => setRetireAge(e.target.value)}
                        className="mt-2 text-base h-12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="monthlyExpense" className="text-base">Current Monthly Expenses (₹)</Label>
                      <Input
                        id="monthlyExpense"
                        type="number"
                        value={monthlyExpense}
                        onChange={(e) => setMonthlyExpense(e.target.value)}
                        className="mt-2 text-base h-12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="inflation" className="text-base">Expected Inflation (%)</Label>
                      <Input
                        id="inflation"
                        type="number"
                        value={inflation}
                        onChange={(e) => setInflation(e.target.value)}
                        className="mt-2 text-base h-12"
                      />
                    </div>
                    <Button 
                      onClick={calculateRetirement}
                      className="w-full bg-[#0E6E6E] hover:bg-[#0E6E6E]/90 text-white h-12 text-base"
                    >
                      Calculate Retirement Corpus
                    </Button>
                  </div>
                  <div>
                    {retireResult && (
                      <div className="bg-gradient-to-br from-[#0E6E6E] to-[#0a5555] p-8 rounded-xl text-white">
                        <h3 className="text-2xl font-bold mb-6">Results</h3>
                        <div className="space-y-4">
                          <div>
                            <p className="text-sm text-gray-200">Years to Retirement</p>
                            <p className="text-3xl font-bold">{retireResult.yearsToRetirement} years</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-200">Monthly Expense at Retirement</p>
                            <p className="text-3xl font-bold">{formatCurrency(retireResult.futureMonthlyExpense)}</p>
                          </div>
                          <div className="pt-4 border-t border-white/30">
                            <p className="text-sm text-gray-200">Corpus Needed</p>
                            <p className="text-4xl font-bold">{formatCurrency(retireResult.corpusNeeded)}</p>
                          </div>
                          <div className="pt-4 border-t border-white/30">
                            <p className="text-sm text-gray-200">Required Monthly SIP (@ 12% returns)</p>
                            <p className="text-3xl font-bold">{formatCurrency(retireResult.monthlySIP)}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* EMI Calculator */}
          <TabsContent value="emi">
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-3xl flex items-center">
                  <Calculator className="w-8 h-8 mr-3 text-[#0E6E6E]" />
                  EMI Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="loanAmount" className="text-base">Loan Amount (₹)</Label>
                      <Input
                        id="loanAmount"
                        type="number"
                        value={loanAmount}
                        onChange={(e) => setLoanAmount(e.target.value)}
                        className="mt-2 text-base h-12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="loanRate" className="text-base">Interest Rate (% per annum)</Label>
                      <Input
                        id="loanRate"
                        type="number"
                        value={loanRate}
                        onChange={(e) => setLoanRate(e.target.value)}
                        className="mt-2 text-base h-12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="loanTenure" className="text-base">Loan Tenure (Years)</Label>
                      <Input
                        id="loanTenure"
                        type="number"
                        value={loanTenure}
                        onChange={(e) => setLoanTenure(e.target.value)}
                        className="mt-2 text-base h-12"
                      />
                    </div>
                    <Button 
                      onClick={calculateEMI}
                      className="w-full bg-[#0E6E6E] hover:bg-[#0E6E6E]/90 text-white h-12 text-base"
                    >
                      Calculate EMI
                    </Button>
                  </div>
                  <div>
                    {emiResult && (
                      <div className="bg-gradient-to-br from-[#0E6E6E] to-[#0a5555] p-8 rounded-xl text-white">
                        <h3 className="text-2xl font-bold mb-6">Results</h3>
                        <div className="space-y-4">
                          <div className="pt-4 border-b border-white/30 pb-4">
                            <p className="text-sm text-gray-200">Monthly EMI</p>
                            <p className="text-4xl font-bold">{formatCurrency(emiResult.emi)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-200">Principal Amount</p>
                            <p className="text-3xl font-bold">{formatCurrency(emiResult.principal)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-200">Total Interest</p>
                            <p className="text-3xl font-bold">{formatCurrency(emiResult.totalInterest)}</p>
                          </div>
                          <div className="pt-4 border-t border-white/30">
                            <p className="text-sm text-gray-200">Total Payment</p>
                            <p className="text-4xl font-bold">{formatCurrency(emiResult.totalPayment)}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Disclaimer */}
        <div className="mt-12 max-w-5xl mx-auto">
          <Card className="bg-gray-100 border-0">
            <CardContent className="p-6">
              <p className="text-sm text-gray-600 text-center">
                <strong>Note:</strong> These calculators provide estimates based on the inputs provided. 
                Actual returns may vary based on market conditions. Please consult with our financial advisors 
                for personalized investment advice.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Calculators;
